import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, FileText, CheckCircle, Loader2 } from "lucide-react";

interface CafUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (cafXml: string) => Promise<void>;
  isUploading: boolean;
}

/**
 * Dialog for uploading CAF (folio authorization) files.
 */
export function CafUploadDialog({
  open,
  onOpenChange,
  onUpload,
  isUploading,
}: CafUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null);
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        processFile(selectedFile);
      }
    },
    [],
  );

  const processFile = (file: File) => {
    if (!file.name.match(/\.xml$/i)) {
      setError("Por favor seleccione un archivo XML");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text.includes("<CAF") && !text.includes("<AUTORIZACION")) {
        setError("El archivo no parece ser un CAF válido");
        return;
      }
      setFile(file);
      setContent(text);
    };
    reader.onerror = () => {
      setError("Error al leer el archivo");
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!content) return;

    try {
      await onUpload(content);
      // Reset state on success
      setFile(null);
      setContent(null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar el CAF");
    }
  };

  const handleClose = () => {
    setFile(null);
    setContent(null);
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cargar CAF</DialogTitle>
          <DialogDescription>
            Suba el archivo CAF (XML) descargado del portal SII para autorizar
            nuevos folios
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
              ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25"}
              ${file ? "border-green-500 bg-green-50" : ""}
            `}
          >
            {file ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle className="h-10 w-10 text-green-500" />
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    setContent(null);
                  }}
                >
                  Cambiar archivo
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center gap-2 cursor-pointer">
                <FileText className="h-10 w-10 text-muted-foreground" />
                <p className="font-medium">Arrastre el archivo CAF aquí</p>
                <p className="text-sm text-muted-foreground">
                  o haga clic para seleccionar
                </p>
                <input
                  type="file"
                  accept=".xml"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Error Message */}
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleUpload} disabled={!content || isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cargando...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Cargar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
